import React, { useEffect, useState } from 'react';
import { taskService } from '../../services/taskService';
import { Task } from '../../types';
import { useToast } from '../../components/Toast';

type NewTaskForm = {
  title: string;
  description: string;
  points: number;
  task_type: Task['task_type'];
  is_active: boolean;
  requirementCount: number;
  order: number;
  required_level: number | '';
};

const initialNewTask: NewTaskForm = {
  title: '',
  description: '',
  points: 0,
  task_type: 'custom',
  is_active: true,
  requirementCount: 1,
  order: 0,
  required_level: '',
};

const AdminTasksPage: React.FC = () => {
  const { showToast } = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTask, setNewTask] = useState<NewTaskForm>(initialNewTask);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editingData, setEditingData] = useState<NewTaskForm>(initialNewTask);
  const [savingEdit, setSavingEdit] = useState(false);

  const activeTasks = tasks.filter((task) => task.is_active);
  const totalPoints = tasks.reduce((sum, task) => sum + (task.points || 0), 0);
  const levelGatedTasks = tasks.filter((task) => task.required_level);

  const systemTaskSteps = [
    {
      title: '1. Tạo nhiệm vụ',
      description: 'Định nghĩa hành động và điểm kinh nghiệm để người dùng tích lũy.',
    },
    {
      title: '2. Cấu hình level',
      description: 'Đặt mốc điểm để mở khóa tính năng (group chat, live stream, v.v.).',
    },
    {
      title: '3. Giám sát & tinh chỉnh',
      description: 'Theo dõi nhiệm vụ đang hoạt động và cập nhật khi cần.',
    },
  ];

  const highlightCards = [
    {
      label: 'Nhiệm vụ đang hoạt động',
      value: activeTasks.length,
    },
    {
      label: 'Tổng điểm thưởng',
      value: totalPoints,
    },
    {
      label: 'Nhiệm vụ yêu cầu level',
      value: levelGatedTasks.length,
    },
  ];

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const data = await taskService.listTasksAdmin();
      setTasks(data.tasks);
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể tải danh sách nhiệm vụ', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const handleCreateTask = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      // Tự động tạo requirements dựa trên task_type
      let requirements = null;
      if (newTask.task_type !== 'custom') {
        requirements = { count: newTask.requirementCount || 1 };
      }

      const payload: any = {
        title: newTask.title.trim(),
        description: newTask.description.trim() || undefined,
        points: parseInt(String(newTask.points)),
        task_type: newTask.task_type,
        is_active: newTask.is_active,
        order: newTask.order || 0,
      };
      if (requirements) payload.requirements = requirements;
      if (newTask.required_level) payload.required_level = Number(newTask.required_level);

      const { task } = await taskService.createTask(payload);
      setTasks((prev) => [task, ...prev]);
      setNewTask(initialNewTask);
      showToast('Tạo nhiệm vụ thành công', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể tạo nhiệm vụ', 'error');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (task: Task) => {
    setEditingTaskId(task.id);
    const reqCount = task.requirements?.count || 1;
    setEditingData({
      title: task.title,
      description: task.description || '',
      points: task.points,
      task_type: task.task_type,
      is_active: task.is_active,
      requirementCount: reqCount,
      order: task.order || 0,
      required_level: task.required_level || '',
    });
  };

  const cancelEdit = () => {
    setEditingTaskId(null);
    setEditingData(initialNewTask);
  };

  const saveEdit = async (taskId: number) => {
    setSavingEdit(true);
    try {
      // Tự động tạo requirements dựa trên task_type
      let requirements = null;
      if (editingData.task_type !== 'custom') {
        requirements = { count: editingData.requirementCount || 1 };
      }

      const payload: any = {
        title: editingData.title.trim(),
        description: editingData.description.trim() || undefined,
        points: parseInt(String(editingData.points)),
        task_type: editingData.task_type,
        is_active: editingData.is_active,
        order: editingData.order || 0,
      };
      if (requirements) payload.requirements = requirements;
      if (editingData.required_level) payload.required_level = Number(editingData.required_level);

      const { task } = await taskService.updateTask(taskId, payload);
      setTasks((list) => list.map((it) => (it.id === taskId ? task : it)));
      cancelEdit();
      showToast('Cập nhật nhiệm vụ thành công', 'success');
    } catch (err: any) {
      showToast(err?.response?.data?.error || 'Không thể cập nhật nhiệm vụ', 'error');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteTask = async (task: Task) => {
    if (!window.confirm(`Xóa nhiệm vụ "${task.title}"?`)) return;
    try {
      await taskService.deleteTask(task.id);
      setTasks((list) => list.filter((it) => it.id !== task.id));
      if (editingTaskId === task.id) {
        cancelEdit();
      }
    } catch {
      showToast('Không thể xóa nhiệm vụ', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-2xl bg-gradient-to-r from-rose-50 via-white to-white border border-rose-100 p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-wide text-rose-500 font-semibold">Nhiệm vụ hệ thống</p>
            <h1 className="text-2xl font-semibold text-gray-900 mt-1">Thiết lập nhiệm vụ để mở khóa tính năng</h1>
            <p className="text-gray-600 mt-2 max-w-2xl">
              Người dùng hoàn thành nhiệm vụ sẽ nhận điểm kinh nghiệm (XP). Khi đủ điểm, họ lên level và mở được các tính năng đặc biệt của hệ thống.
              Đây là luồng nhiệm vụ tách biệt hoàn toàn với bài tập khóa học/video.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full md:w-auto">
            {highlightCards.map((card) => (
              <div key={card.label} className="bg-white rounded-xl border border-rose-100/70 px-4 py-3 shadow-sm">
                <p className="text-xs text-gray-500">{card.label}</p>
                <p className="text-xl font-semibold text-gray-900">{card.value}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="grid gap-4 md:grid-cols-3 mt-6">
          {systemTaskSteps.map((step, idx) => (
            <div key={step.title} className="bg-white rounded-xl border border-gray-100 px-4 py-3 shadow-sm flex gap-3">
              <div className="h-8 w-8 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center font-semibold">{idx + 1}</div>
              <div>
                <p className="font-medium text-gray-900">{step.title}</p>
                <p className="text-sm text-gray-600">{step.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 bg-white shadow rounded-2xl border border-gray-200">
          <div className="p-5 border-b border-gray-100 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Tạo nhiệm vụ hệ thống</h2>
              <p className="text-sm text-gray-500">Thiết lập nhiệm vụ để người dùng tích điểm và mở khóa tính năng</p>
            </div>
            <span className="text-xs bg-gray-100 text-gray-600 px-3 py-1 rounded-full uppercase tracking-wide">XP & Level</span>
          </div>
          <form className="p-5 space-y-5" onSubmit={handleCreateTask}>
            <div className="grid md:grid-cols-2 gap-4">
              <label className="text-sm">
                <span className="text-gray-700 font-medium">Tiêu đề nhiệm vụ*</span>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-200"
                  value={newTask.title}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="VD: Đăng 3 bài chia sẻ mỗi tuần"
                  required
                />
              </label>
              <label className="text-sm">
                <span className="text-gray-700 font-medium">Điểm thưởng (XP)*</span>
                <input
                  type="number"
                  min="0"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-200"
                  value={newTask.points}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, points: parseInt(e.target.value) || 0 }))}
                  required
                />
              </label>
            </div>

            <label className="text-sm block">
              <span className="text-gray-700 font-medium">Mô tả cho người dùng</span>
              <textarea
                className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-200"
                rows={3}
                value={newTask.description}
                onChange={(e) => setNewTask((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Giải thích quy tắc, ví dụ cách hoàn thành..."
              />
            </label>

            <div className="grid md:grid-cols-2 gap-4">
              <label className="text-sm">
                <span className="text-gray-700 font-medium">Loại nhiệm vụ*</span>
                <select
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-200"
                  value={newTask.task_type}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, task_type: e.target.value as Task['task_type'] }))}
                >
                  <option value="post">Đăng bài</option>
                  <option value="comment">Bình luận</option>
                  <option value="enrollment">Ghi danh khóa học</option>
                  <option value="video_watch">Xem video</option>
                  <option value="upload">Upload nội dung</option>
                  <option value="custom">Tùy chỉnh</option>
                </select>
              </label>
              <label className="text-sm">
                <span className="text-gray-700 font-medium">Level yêu cầu (tùy chọn)</span>
                <input
                  type="number"
                  min="1"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-200"
                  value={newTask.required_level || ''}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, required_level: e.target.value ? Number(e.target.value) : '' }))}
                  placeholder="Để trống = ai cũng thấy"
                />
                <p className="text-xs text-gray-500 mt-1">Dùng để khóa nhiệm vụ cho các level cao hơn.</p>
              </label>
            </div>

            {newTask.task_type !== 'custom' && (
              <label className="text-sm block">
                <span className="text-gray-700 font-medium">
                  Số lượng yêu cầu {newTask.task_type === 'post' && '(số bài viết)'}
                  {newTask.task_type === 'comment' && '(số bình luận)'}
                  {newTask.task_type === 'enrollment' && '(số khóa học)'}
                  {newTask.task_type === 'video_watch' && '(số video)'}
                </span>
                <input
                  type="number"
                  min="1"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-200"
                  value={newTask.requirementCount}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, requirementCount: parseInt(e.target.value) || 1 }))}
                />
              </label>
            )}

            <div className="grid md:grid-cols-3 gap-4">
              <label className="text-sm">
                <span className="text-gray-700 font-medium">Thứ tự hiển thị</span>
                <input
                  type="number"
                  min="0"
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-200"
                  value={newTask.order}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  placeholder="0"
                />
              </label>
              <label className="text-sm">
                <span className="text-gray-700 font-medium">Trạng thái</span>
                <select
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-rose-200"
                  value={newTask.is_active ? 'true' : 'false'}
                  onChange={(e) => setNewTask((prev) => ({ ...prev, is_active: e.target.value === 'true' }))}
                >
                  <option value="true">Kích hoạt</option>
                  <option value="false">Ẩn tạm thời</option>
                </select>
              </label>
            </div>

            <div className="flex justify-between items-center border rounded-xl border-gray-100 bg-gray-50 p-4 text-sm text-gray-600">
              <div>
                <p className="font-medium text-gray-900">Khác gì với nhiệm vụ khóa học?</p>
                <p>Nhiệm vụ hệ thống để tăng XP mở tính năng. Nhiệm vụ khóa học dùng để khóa video tiếp theo.</p>
              </div>
              <a
                href="/admin/course-tasks"
                className="inline-flex items-center px-4 py-2 text-sm font-medium border rounded-lg text-rose-600 border-rose-200 hover:bg-rose-50 transition"
              >
                Quản lý nhiệm vụ khóa học →
              </a>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-lg font-medium transition disabled:opacity-50"
                disabled={creating}
              >
                {creating ? 'Đang tạo...' : 'Lưu nhiệm vụ'}
              </button>
            </div>
          </form>
        </div>

        <aside className="bg-white shadow rounded-2xl border border-gray-200 p-5 space-y-4">
          <div>
            <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Lưu ý</p>
            <h3 className="text-lg font-semibold text-gray-900 mt-1">Nhiệm vụ hệ thống = mở khóa tính năng</h3>
            <p className="text-sm text-gray-600 mt-2">
              Đừng dùng form này để tạo bài tập video. Luồng khóa học đã có trang riêng với việc nộp bài & duyệt bài để mở video tiếp theo.
            </p>
          </div>
          <div className="bg-gray-50 border border-dashed border-gray-200 rounded-lg p-4 text-sm text-gray-600 space-y-2">
            <p>✅ Dùng cho: nhiệm vụ tuần, nhiệm vụ cộng đồng, nhiệm vụ mở chat, quyền đăng bài…</p>
            <p>❌ Không dùng cho: bài tập video cần nộp file/đợi duyệt (hãy qua mục khóa học).</p>
          </div>
        </aside>
      </section>

      <section className="bg-white shadow rounded-2xl border border-gray-200">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Danh sách nhiệm vụ hệ thống</h2>
            <p className="text-sm text-gray-500">Quản lý nhiệm vụ & điều kiện mở khóa tính năng</p>
          </div>
          <button
            type="button"
            className="text-sm text-rose-600 hover:text-rose-700"
            onClick={fetchTasks}
            disabled={loading}
          >
            {loading ? 'Đang tải...' : 'Làm mới'}
          </button>
        </div>
        <div className="p-5 overflow-x-auto">
          {loading ? (
            <div className="text-center py-10 text-gray-500">Đang tải danh sách nhiệm vụ...</div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-10 text-gray-500">Chưa có nhiệm vụ nào. Hãy tạo nhiệm vụ đầu tiên!</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 uppercase text-xs tracking-wide border-b border-gray-100">
                  <th className="py-3 pr-4">ID</th>
                  <th className="py-3 pr-4">Tiêu đề</th>
                  <th className="py-3 pr-4">Điểm</th>
                  <th className="py-3 pr-4">Loại</th>
                  <th className="py-3 pr-4">Level yêu cầu</th>
                  <th className="py-3 pr-4">Thứ tự</th>
                  <th className="py-3 pr-4">Trạng thái</th>
                  <th className="py-3 pr-4 text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => {
                  const isEditing = editingTaskId === task.id;
                  return (
                    <tr key={task.id} className="border-b border-gray-50">
                      <td className="py-3 pr-4 font-medium text-gray-900">{task.id}</td>
                      <td className="py-3 pr-4">
                        {isEditing ? (
                          <div className="space-y-2">
                            <input
                              className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                              value={editingData.title}
                              onChange={(e) => setEditingData((prev) => ({ ...prev, title: e.target.value }))}
                            />
                            <textarea
                              className="border border-gray-300 rounded-lg px-2 py-1 w-full text-sm"
                              rows={2}
                              value={editingData.description}
                              onChange={(e) => setEditingData((prev) => ({ ...prev, description: e.target.value }))}
                            />
                          </div>
                        ) : (
                          <div>
                            <p className="font-medium text-gray-900">{task.title}</p>
                            <p className="text-xs text-gray-500">{task.description || '—'}</p>
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {isEditing ? (
                          <input
                            type="number"
                            min="0"
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingData.points}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, points: parseInt(e.target.value) || 0 }))}
                          />
                        ) : (
                          <span className="font-semibold text-gray-900">{task.points} XP</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {isEditing ? (
                          <select
                            className="border border-gray-300 rounded-lg px-2 py-1"
                            value={editingData.task_type}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, task_type: e.target.value as Task['task_type'] }))}
                          >
                            <option value="post">Đăng bài</option>
                            <option value="comment">Bình luận</option>
                            <option value="enrollment">Ghi danh</option>
                            <option value="video_watch">Xem video</option>
                            <option value="upload">Upload nội dung</option>
                            <option value="custom">Tùy chỉnh</option>
                          </select>
                        ) : (
                          <span className="px-2 py-1 rounded-full bg-rose-50 text-rose-600 text-xs font-semibold">
                            {task.task_type}
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {isEditing ? (
                          <input
                            type="number"
                            min="1"
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingData.required_level || ''}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, required_level: e.target.value ? Number(e.target.value) : '' }))}
                            placeholder="Không yêu cầu"
                          />
                        ) : (
                          <span className="text-gray-700">
                            {task.required_level ? `Level ${task.required_level}+` : 'Mặc định'}
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {isEditing ? (
                          <input
                            type="number"
                            min="0"
                            className="border border-gray-300 rounded-lg px-2 py-1 w-full"
                            value={editingData.order}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                          />
                        ) : (
                          <span className="text-gray-700">{task.order || 0}</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        {isEditing ? (
                          <select
                            className="border border-gray-300 rounded-lg px-2 py-1"
                            value={editingData.is_active ? 'true' : 'false'}
                            onChange={(e) => setEditingData((prev) => ({ ...prev, is_active: e.target.value === 'true' }))}
                          >
                            <option value="true">Kích hoạt</option>
                            <option value="false">Vô hiệu hóa</option>
                          </select>
                        ) : task.is_active ? (
                          <span className="px-2 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold">Kích hoạt</span>
                        ) : (
                          <span className="px-2 py-1 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold">Vô hiệu hóa</span>
                        )}
                      </td>
                      <td className="py-3 pl-4 text-right space-x-2 whitespace-nowrap">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => saveEdit(task.id)}
                              className="px-3 py-1 rounded-lg bg-green-600 text-white text-sm"
                              disabled={savingEdit}
                            >
                              {savingEdit ? 'Đang lưu...' : 'Lưu'}
                            </button>
                            <button onClick={cancelEdit} className="px-3 py-1 rounded-lg bg-gray-200 text-gray-700 text-sm">
                              Hủy
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(task)}
                              className="px-3 py-1 rounded-lg bg-blue-600 text-white text-sm"
                            >
                              Sửa
                            </button>
                            <button
                              onClick={() => handleDeleteTask(task)}
                              className="px-3 py-1 rounded-lg bg-red-600 text-white text-sm"
                            >
                              Xóa
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminTasksPage;

